import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import {
  changeAdminUserStatus,
  createAdminUser,
  listAdminUsers,
  listRoles,
  replaceAdminUserRoles,
  updateAdminUser,
} from "../../services/accessControlService";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const emptyForm = {
  id: null,
  name: "",
  email: "",
  password: "",
  status: "active",
  roleIds: [],
};

const AdminUsersPage = () => {
  const { hasPermission, refreshUser, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const canCreate =
    hasPermission("users.create") && hasPermission("users.assign_roles");
  const canUpdate = hasPermission("users.update");
  const canAssign = hasPermission("users.assign_roles");
  const canChangeStatus = hasPermission("users.change_status");

  const loadData = useCallback(async (searchTerm = "") => {
    try {
      const [usersResponse, rolesResponse] = await Promise.all([
        listAdminUsers(searchTerm),
        listRoles(),
      ]);
      setUsers(usersResponse.data || []);
      setRoles(rolesResponse.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load administrators");
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => loadData(search), 250);
    return () => clearTimeout(timer);
  }, [search, loadData]);

  const openCreate = () => {
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (user) => {
    setForm({
      id: user.id,
      name: user.name,
      email: user.email,
      password: "",
      status: user.status,
      roleIds: user.roles.map((role) => role.id),
    });
    setOpen(true);
  };

  const toggleRole = (roleId, checked) => {
    setForm((current) => ({
      ...current,
      roleIds: checked
        ? [...new Set([...current.roleIds, roleId])]
        : current.roleIds.filter((id) => id !== roleId),
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!form.id && form.password.length < 8) {
      toast.error("Password must contain at least 8 characters");
      return;
    }
    if (form.status === "active" && form.roleIds.length === 0) {
      toast.error("Assign at least one role");
      return;
    }
    try {
      setSaving(true);
      if (form.id) {
        if (canUpdate) {
          await updateAdminUser(form.id, {
            name: form.name,
            email: form.email,
            password: form.password || undefined,
          });
        }
        if (canAssign) {
          await replaceAdminUserRoles(form.id, form.roleIds);
        }
        toast.success("Administrator updated");
      } else {
        await createAdminUser({
          name: form.name,
          email: form.email,
          password: form.password,
          status: form.status,
          role_ids: form.roleIds,
        });
        toast.success("Administrator created");
      }
      if (Number(form.id) === Number(currentUser.id)) await refreshUser();
      setOpen(false);
      loadData(search);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save administrator");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (user) => {
    try {
      const status = user.status === "active" ? "inactive" : "active";
      await changeAdminUserStatus(user.id, status);
      toast.success(`Administrator ${status === "active" ? "activated" : "deactivated"}`);
      if (Number(user.id) === Number(currentUser.id)) {
        if (status === "inactive") {
          window.location.href = "/login";
          return;
        }
        await refreshUser();
      }
      loadData(search);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to change status");
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="flex items-center gap-3 text-3xl font-bold text-slate-900">
            <Users className="text-primary" /> Admin Users
          </h1>
          <p className="mt-1 text-slate-500">
            Create administrator accounts and assign multiple roles.
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="rounded-2xl">
            <Plus size={18} className="mr-2" /> New Administrator
          </Button>
        )}
      </div>

      <div className="relative mb-5 max-w-md">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          size={18}
        />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search name or email..."
          className="pl-10"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Administrator</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-12 text-center text-slate-500">
                  No administrators found.
                </TableCell>
              </TableRow>
            )}
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="font-semibold text-slate-900">{user.name}</div>
                  <div className="text-xs text-slate-500">{user.email}</div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {user.roles.map((role) => (
                      <Badge key={role.id} variant="outline">
                        {role.name}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={
                      user.status === "active"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-200 text-slate-600"
                    }
                  >
                    {user.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    {(canUpdate || canAssign) && (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(user)}>
                        <Pencil size={16} />
                      </Button>
                    )}
                    {canChangeStatus && (
                      <Button variant="outline" size="sm" onClick={() => toggleStatus(user)}>
                        {user.status === "active" ? "Deactivate" : "Activate"}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {form.id ? "Edit Administrator" : "Create Administrator"}
            </DialogTitle>
            <DialogDescription>
              Active administrators must have at least one role.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-semibold">Name</label>
              <Input
                value={form.name}
                disabled={Boolean(form.id) && !canUpdate}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold">Email</label>
              <Input
                type="email"
                value={form.email}
                disabled={Boolean(form.id) && !canUpdate}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-sm font-semibold">
                Password {form.id && "(leave blank to keep current password)"}
              </label>
              <Input
                type="password"
                value={form.password}
                disabled={Boolean(form.id) && !canUpdate}
                onChange={(event) =>
                  setForm({ ...form, password: event.target.value })
                }
              />
            </div>
            {!form.id && (
              <div className="md:col-span-2">
                <label className="mb-1 block text-sm font-semibold">Status</label>
                <select
                  value={form.status}
                  onChange={(event) =>
                    setForm({ ...form, status: event.target.value })
                  }
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 text-sm font-semibold">Roles</div>
            <div className="grid gap-2 rounded-xl border border-slate-200 p-4 md:grid-cols-2">
              {roles.map((role) => (
                <label key={role.id} className="flex items-center gap-2">
                  <Checkbox
                    checked={form.roleIds.includes(role.id)}
                    disabled={Boolean(form.id) && !canAssign}
                    onCheckedChange={(checked) => toggleRole(role.id, Boolean(checked))}
                  />
                  <span>
                    <span className="font-medium text-slate-800">{role.name}</span>
                    <span className="ml-2 text-xs text-slate-400">
                      {role.permission_count} permissions
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Administrator"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
